import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Post } from '../types';
import { postsService } from '../services/posts.service';
import { useAuth } from './AuthContext';

const POSTS_CHANGED_EVENT = 'intranet:postsChanged';

/** Background poll interval — avoid hammering the API and flashing the feed every few seconds */
const POSTS_POLL_INTERVAL_MS = 60_000;

export const emitPostsChanged = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(POSTS_CHANGED_EVENT));
};

export type RefreshPostsOptions = {
  /** When true, do not toggle loading state (no full-feed skeleton on poll) */
  silent?: boolean;
};

type PostsContextType = {
  posts: Post[];
  isLoading: boolean;
  error: string | null;
  refreshPosts: (options?: RefreshPostsOptions) => Promise<void>;
  lastUpdatedAt: number | null;
};

const PostsContext = createContext<PostsContextType | undefined>(undefined);

export const PostsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const inFlightRef = useRef(false);

  const refreshPosts = useCallback(async (options?: RefreshPostsOptions) => {
    if (!isAuthenticated) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const silent = options?.silent === true;
    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const data = await postsService.getAll();
      setPosts(data);
      setLastUpdatedAt(Date.now());
    } catch (e: any) {
      if (!silent) {
        setError(e?.response?.data?.message || e?.message || 'Failed to load posts');
      }
    } finally {
      if (!silent) setIsLoading(false);
      inFlightRef.current = false;
    }
  }, [isAuthenticated]);

  // Initial load + polling (cross-user "real-time")
  useEffect(() => {
    if (!isAuthenticated) {
      setPosts([]);
      setLastUpdatedAt(null);
      return;
    }

    refreshPosts();
    const interval = window.setInterval(
      () => refreshPosts({ silent: true }),
      POSTS_POLL_INTERVAL_MS,
    );

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshPosts({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      // StrictMode remounts before the first fetch finishes; without this the next
      // refreshPosts() sees inFlightRef still true and skips — feed stays empty.
      inFlightRef.current = false;
    };
  }, [isAuthenticated, refreshPosts]);

  // Local instant refresh (same user session)
  useEffect(() => {
    const handler = () => {
      refreshPosts({ silent: true });
    };
    window.addEventListener(POSTS_CHANGED_EVENT, handler as EventListener);
    return () => window.removeEventListener(POSTS_CHANGED_EVENT, handler as EventListener);
  }, [refreshPosts]);

  const value = useMemo<PostsContextType>(() => ({
    posts,
    isLoading,
    error,
    refreshPosts,
    lastUpdatedAt,
  }), [posts, isLoading, error, refreshPosts, lastUpdatedAt]);

  return <PostsContext.Provider value={value}>{children}</PostsContext.Provider>;
};

export const usePosts = (): PostsContextType => {
  const ctx = useContext(PostsContext);
  if (!ctx) throw new Error('usePosts must be used within a PostsProvider');
  return ctx;
};

